import { useEffect } from 'react';
import {
	getSequences,
	retryInitiateSequence
} from '@/services/sequencesService';
import toast from 'react-hot-toast';
import { useSequencesContext } from '../SequencesContext';
import { SEQUENCES_PER_PAGE } from '../config';

export type StepsType = {
	id?: string;
	subject: string;
	body: string;
	sendAfterInterval: number;
	sendAfterType: string;
	order: number;
	justCreated?: boolean;
};

export type BusinessType = {
	id: string;
	createdAt: string;
	name: string;
	score: number;
	categories: string;
	step: number;
	campaignId: string;
	listOriginId: string;
	listOriginName: string;
	locations: number;
	failReason?: string | null;
	status?: 'ACTIVE' | 'HALTED' | 'COMPLETED' | 'REPLIED';
};

export type StatusType = 'ACTIVE' | 'IDLE' | 'DISABLED' | 'DRAFT' | undefined;

export type SequencesType = {
	id?: string;
	name: string;
	status: StatusType;
	steps: StepsType[];
	businesses?: BusinessType[];
	page?: number;
	justCreated?: boolean; // This flag is set to true when the user creates a new sequence, so it keeps the Add business tab even when it has an id
	updatedAt?: string;
	creationSource?: 'User' | 'AI';
	analytics?: {
		totalBusinessCount: number;
		activeBusinessCount: number;
		replyCount: number;
		openRate: number;
	};
};

export function useSequences() {
	const {
		currentPage,
		sequences,
		sequenceSearch,
		selectedSequence,
		setSelectedLists,
		setBusinessesInLists,
		setUnapprovedBusinesses,
		setBusinessesToRemove,
		setSelectedBusinesses,
		setSequences,
		setSelectedSequence,
		sequenceEnroll,
		setHasMoreBusinessesToFetch,
		setBusinessesAlreadyInSequence,
		setBusinessesScrollPage,
		setAllBusinessesSelect,
		setBusinessesToInclude,
		setSelectedTemplate
	} = useSequencesContext();

	async function fetchSequences() {
		const [sequenceRes] = await Promise.all([getSequences()]);

		if (sequenceRes.data) {
			setSequences(sequenceRes.data.data.campaigns);
		}
	}

	useEffect(() => {
		if (
			sequences &&
			sequences.length > 0 &&
			!selectedSequence &&
			!sequenceEnroll
		) {
			setSelectedSequence({
				name: 'New sequence',
				steps: [],
				status: 'DRAFT'
			});
		}
	}, [sequences, selectedSequence, sequenceEnroll, setSelectedSequence]);

	function getPaginatedSequences() {
		const filteredSequences = sequences?.filter(s => {
			return s.name.toLowerCase().includes(sequenceSearch.toLowerCase());
		});

		const paginatedSequences = filteredSequences?.slice(
			(currentPage - 1) * SEQUENCES_PER_PAGE,
			currentPage * SEQUENCES_PER_PAGE
		);
		return paginatedSequences;
	}

	function retryInitiateBusinesses() {
		if (!selectedSequence?.id) return;
		retryInitiateSequence({
			campaignId: selectedSequence.id
		}).then(() => {
			toast.success(
				'Retry send emails to businesses requested successfully!'
			);
			fetchSequences();
		});
	}

	function resetSequenceState() {
		setSelectedLists([]);
		setBusinessesInLists([]);
		setUnapprovedBusinesses([]);
		setBusinessesToRemove([]);
		setSelectedBusinesses([]);
		setHasMoreBusinessesToFetch(true);
		setBusinessesAlreadyInSequence(new Set<string>());
		setBusinessesScrollPage(1);
		setAllBusinessesSelect('SELECTED_ALL');
		setBusinessesToInclude([]);
		setSelectedTemplate(undefined);
		setSelectedSequence(undefined);
	}

	return {
		fetchSequences,
		getPaginatedSequences,
		retryInitiateBusinesses,
		resetSequenceState
	};
}
